import { Close, Search } from "@mui/icons-material";
import { Autocomplete, TextField, Box, Paper, Typography, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { logoutFilterHandler, storeHandler } from "../../api/store/storeActions";
import { useEffect, useState, useMemo, useRef } from "react";
import { useGetSearchSuggestionsQuery } from "../../api/searchSuggestions/searchSuggestionsSlice";
import { useLocation } from "react-router-dom";
import "./searchBar.css";

interface props {
  setRegex?: Function;
  setKeyword?: Function;
  setPageNo?: Function;
  regex?: string;
  disableSuggestions?: boolean;
}

function SearchBar({
  setRegex,
  setKeyword,
  setPageNo,
  regex,
  disableSuggestions,
}: props) {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith("/admin");
  const suggestionsDisabled = !!disableSuggestions || isAdminPath;
  const [searchValue, setSearchValue] = useState<string>(regex || "");
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  // Get selected media types from Redux
  const selectedMediaTypes = useSelector((state: any) => state.data.mediaType) || [];
  const selectedCreativeIds = useSelector((state: any) => state.data.selectedCreativeIds) || [];
  // Check if current path is admin or home (affects layout only)
  const isAdminOrHome = location.pathname === '/admin' || location.pathname === '/home';
  // Track previous search value to detect media type changes
  const prevSearchValueRef = useRef<string>("");
  const skipFilterResetRef = useRef(false);

  // RTK Query hook for search suggestions - only on admin or home paths
  const { data: suggestionsData, isLoading: loading } =
    useGetSearchSuggestionsQuery(
      { searchKeyword, mediaType: selectedMediaTypes },
      {
        skip: suggestionsDisabled || searchKeyword.length < 3,
      }
    );

  const allSuggestions = suggestionsData?.data || [];

  // Load search history from localStorage on component mount
  useEffect(() => {
    const savedHistory = JSON.parse(localStorage.getItem('searchHistory') || "[]");
    if (savedHistory.length > 0) {
      setSearchHistory(savedHistory);
    }

    // Load last selected suggestion from localStorage
    const savedSuggestion = localStorage.getItem('lastSelectedSuggestion');
    if (savedSuggestion) {
      try {
        const parsed = JSON.parse(savedSuggestion);
        lastSelectedSuggestionRef.current = parsed;
      } catch (e) {
        console.error('Failed to parse saved suggestion:', e);
      }
    }
  }, []);

  // Save search history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // Helper function to detect and extract media type from search query
  const detectMediaType = (query: string): { mediaType: string | null; cleanQuery: string } => {
    const lowerQuery = query.toLowerCase().trim();
    const mediaTypeKeywords = {
      video: ['video', 'videos'],
      image: ['image', 'images', 'img', 'picture', 'pictures', 'photo', 'photos'],
      ppt: ['ppt', 'powerpoint', 'presentation', 'presentations']
    };

    // Check for media type keywords (exact match or as a word)
    for (const [mediaType, keywords] of Object.entries(mediaTypeKeywords)) {
      for (const keyword of keywords) {
        // Match whole word or exact match
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(query)) {
          // DON'T remove the media type keyword - keep the original query
          // This allows searching for titles that contain words like "Video"
          return { mediaType, cleanQuery: query };
        }
      }
    }

    return { mediaType: null, cleanQuery: query };
  };

  const preserveSelectedCreativeRef = useRef(false);
  const lastSelectedSuggestionRef = useRef<{ value: string; title?: string } | null>(null);
  const lastHistoryTermRef = useRef<string>("");

  function regexSet(
    value: string,
    options: { preserveSelected?: boolean; skipAutoClear?: boolean } = {}
  ) {
    setSearchValue(value);
    if (options.skipAutoClear) {
      skipFilterResetRef.current = true;
    }
    const shouldPreserveSelected = options.preserveSelected || preserveSelectedCreativeRef.current;
    if (!shouldPreserveSelected && selectedCreativeIds.length > 0) {
      dispatch(storeHandler({ selectedCreativeIds: [] }));
      lastSelectedSuggestionRef.current = null;
      localStorage.removeItem('lastSelectedSuggestion');
      localStorage.removeItem('lastSelectedMetadata');
      localStorage.removeItem('selectedCreativeIds');
    }
    if (preserveSelectedCreativeRef.current) {
      preserveSelectedCreativeRef.current = false;
    }
    if (!shouldPreserveSelected) {
      lastSelectedSuggestionRef.current = null;
      localStorage.removeItem('lastSelectedSuggestion');
      localStorage.removeItem('lastSelectedMetadata');
      localStorage.removeItem('selectedCreativeIds');
    }

    prevSearchValueRef.current = value;

    // Check if input has 3 or more characters and set search keyword for RTK Query
    if (value.length >= 3) {
      setSearchKeyword(value);
    } else {
      setSearchKeyword("");
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      if (skipFilterResetRef.current) {
        skipFilterResetRef.current = false;
      } else {
        // logoutFilterHandler now preserves mediaType automatically
        dispatch(logoutFilterHandler());
      }
      setPageNo && setPageNo(1);
      // Use the search value directly without modification
      const fallbackQuery =
        searchValue ||
        lastSelectedSuggestionRef.current?.title ||
        lastSelectedSuggestionRef.current?.value ||
        searchValue;
      setRegex && setRegex(fallbackQuery);
      setKeyword && setKeyword(fallbackQuery || "");

      // Persist actual search term into history only for non-admin paths
      if (
        !isAdminPath &&
        fallbackQuery &&
        fallbackQuery.trim() &&
        fallbackQuery !== lastHistoryTermRef.current
      ) {
        lastHistoryTermRef.current = fallbackQuery;
        addToHistory(fallbackQuery);
      }
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(handler); // Clear timeout if the user types again
    };
  }, [searchValue, dispatch, setPageNo, setRegex, setKeyword]);

  useEffect(() => {
    if (regex === "") {
      setKeyword && setKeyword(regex);
      setSearchValue("");
      if (selectedCreativeIds.length > 0) {
        dispatch(storeHandler({ selectedCreativeIds: [] }));
      }
      lastSelectedSuggestionRef.current = null;
      localStorage.removeItem('lastSelectedSuggestion');
      localStorage.removeItem('lastSelectedMetadata');
      localStorage.removeItem('selectedCreativeIds');
      localStorage.removeItem('selectedMediaType');
    }
  }, [regex, dispatch, selectedCreativeIds.length]);

  const addToHistory = (searchTerm: string) => {
    if (searchTerm.trim()) {
      const newHistory = [searchTerm, ...searchHistory.filter(item => item !== searchTerm)].slice(0, 4);
      setSearchHistory(newHistory);
    }
  };

  const removeFromHistory = (index: number) => {
    const newHistory = searchHistory.filter((_, i) => i !== index);
    setSearchHistory(newHistory);
  };

  const clearAllHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Clear both state and localStorage
    const emptyHistory: string[] = [];
    setSearchHistory(emptyHistory);
    localStorage.setItem('searchHistory', JSON.stringify(emptyHistory));
    // Force a small delay to ensure state update is processed
    // The dropdown will close automatically when searchHistory becomes empty
    // which is expected behavior since there's nothing to show
  };

  function handleSubmit(value: string) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Search_${value}`,
        category: "User Interaction",
        action: "Button Click",
        label: `Search Button`,
      });
    }

    // Only record history on non-admin paths
    if (!isAdminPath) {
      addToHistory(value);
    }
    lastSelectedSuggestionRef.current = null;
    localStorage.removeItem('lastSelectedSuggestion');
    localStorage.removeItem('lastSelectedMetadata');
    localStorage.removeItem('selectedCreativeIds');
    if (selectedCreativeIds.length > 0) {
      dispatch(storeHandler({ selectedCreativeIds: [] }));
    }
    // Use the search value directly
    setKeyword && setKeyword(value);
  }

  const selectSuggestion = (option: any) => {
    if (!option) {
      return;
    }
    // Use title as the primary value, fallback to value if title is not available
    const suggestionValue = option.title || option.value || "";
    preserveSelectedCreativeRef.current = true;
    const suggestionData = { value: suggestionValue, title: option.title };
    lastSelectedSuggestionRef.current = suggestionData;
    // Persist to localStorage for page refresh
    localStorage.setItem('lastSelectedSuggestion', JSON.stringify(suggestionData));
    regexSet(suggestionValue, { preserveSelected: true });

    // Set media type from suggestion metadata if available
    let selectedMediaType: string[] = [];
    if (option.mediaType) {
      selectedMediaType = [option.mediaType];
      dispatch(storeHandler({ mediaType: selectedMediaType }));
    }

    // Persist media type to localStorage for page refresh
    if (selectedMediaType.length > 0) {
      localStorage.setItem('selectedMediaType', JSON.stringify(selectedMediaType));
    }

    // Only record history on non-admin paths
    if (!isAdminPath) {
      addToHistory(suggestionValue);
    }
    const fallbackKeyword = option.title || suggestionValue;
    // Use the suggestion value directly
    setKeyword && setKeyword(fallbackKeyword);
    setSearchKeyword("");
    if (option.id) {
      const creativeIds = [option.id];
      dispatch(storeHandler({ selectedCreativeIds: creativeIds }));
      // Persist to localStorage for page refresh
      localStorage.setItem('selectedCreativeIds', JSON.stringify(creativeIds));
    } else {
      dispatch(storeHandler({ selectedCreativeIds: [] }));
      localStorage.removeItem('selectedCreativeIds');
    }

  };

  // Combine suggestions and history for autocomplete options
  // Use useMemo to recalculate when selectedMediaTypes, suggestions, or searchValue changes
  const options = useMemo(() => {
    if (suggestionsDisabled) {
      return [];
    }

    const optionsList: any[] = [];

    if (allSuggestions.length > 0 && searchValue.length >= 3) {
      const seenValues = new Set<string>();

      allSuggestions.forEach((suggestion: any) => {
        const suggestionValue = suggestion.title || suggestion.matchedString;
        if (!suggestionValue) {
          return;
        }
        // Avoid duplicate suggestions by normalized value
        const normalizedValue = suggestionValue.trim().toLowerCase();
        if (seenValues.has(normalizedValue)) {
          return;
        }
        seenValues.add(normalizedValue);
        if (selectedMediaTypes.length > 0) {
          const suggestionMediaType = suggestion.mediaType?.toLowerCase()?.trim();
          const matchesFilter = selectedMediaTypes.some((selectedType: string) => {
            const normalizedSelected = selectedType.toLowerCase().trim();
            const normalizedSuggestion =
              suggestionMediaType === "images"
                ? "image"
                : suggestionMediaType === "videos"
                  ? "video"
                  : suggestionMediaType === "ppts"
                    ? "ppt"
                    : suggestionMediaType;
            return normalizedSelected === normalizedSuggestion;
          });
          if (!matchesFilter) {
            return;
          }
        }

        optionsList.push({
          type: "creative",
          value: suggestionValue,
          mediaType: suggestion.mediaType,
          id: suggestion.id,
          title: suggestion.title,
          newTitle: suggestion.newTitle,
          metadata: suggestion.metadata,
        });
      });
    }

    if (
      (allSuggestions.length === 0 || searchValue === "") &&
      searchHistory &&
      searchHistory.length > 0
    ) {
      searchHistory.forEach((item) => {
        if (item && item.trim()) {
          optionsList.push({
            type: "history",
            value: item,
          });
        }
      });
    }

    return optionsList;
  }, [
    allSuggestions,
    searchValue,
    selectedMediaTypes,
    searchHistory,
    suggestionsDisabled,
  ]);

  // Use integrated styling only on home; admin gets bordered box for visibility
  const isIntegrated = location.pathname === "/home";
  const containerClasses = [
    isIntegrated
      ? "w-full h-full border-0 rounded-none bg-transparent px-3"
      : "w-full lg:w-2/6 min-w-[15rem] h-[5vh] min-h-[2.5rem] bg-white border rounded-xl my-auto flex items-center justify-between px-4 relative",
    isIntegrated ? "my-auto flex items-center relative" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      <Autocomplete
        freeSolo
        value={searchValue}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            regexSet(newValue);
            handleSubmit(newValue);
          } else if (newValue && typeof newValue === 'object') {
            selectSuggestion(newValue);
          }
        }}
        onInputChange={(event, newInputValue) => {
          regexSet(newInputValue);
        }}
        options={options}
        // Disable client-side filtering so server suggestions show even if text doesn't match exactly
        filterOptions={(optionsList) => optionsList}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.title || option.value;
        }}
        renderOption={(props, option) => {
          return (
            <Box
              component="li"
              {...props}
              className={`search-option-row ${props.className || ""}`}
            >
              {option.type === "creative" ? (
                <Box className="search-suggestion">
                  <Box className="search-suggestion__header">
                    <Typography
                      className="search-suggestion__title"
                      title={option.newTitle || option.title || option.value}
                    >
                      {option.newTitle || option.title || option.value}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box className="search-history-option">
                  <Typography className="search-history-option__text">
                    {option.value}
                  </Typography>
                </Box>
              )}
              {option.type === "history" && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const index = searchHistory.indexOf(option.value);
                    if (index > -1) removeFromHistory(index);
                  }}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Close style={{ fontSize: "1rem" }} />
                </IconButton>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for..."
            variant="standard"
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              className: "border-none w-full active:outline-none focus-visible:outline-none",
              startAdornment: isIntegrated ? (
                <img
                  src="/img/Layer_1.png"
                  alt="Search"
                  className="mr-2 ml-1"
                  style={{ width: '17.4px', height: '17.39px', objectFit: 'contain' }}
                />
              ) : null
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '100%',
                letterSpacing: '0%',
                color: '#000000',
                ...(isIntegrated && { padding: 0 })
              }
            }}
          />
        )}
        loading={loading}
        PaperComponent={
          suggestionsDisabled
            ? undefined
            : ({ children }) => (
              <Paper
                elevation={0}
                className="w-full rounded-lg border border-gray-100 bg-white shadow-lg overflow-hidden"
              >
                {options.length > 0 && searchValue.length >= 3 && (
                  <Box className="suggestions-header">
                    <Typography variant="body2">Suggestions</Typography>
                  </Box>
                )}
                {searchHistory.length > 0 &&
                  (allSuggestions.length === 0 || searchValue === "") &&
                  !loading && (
                    <>
                      <Box
                        className="flex items-center justify-between p-2 border-b border-gray-100"
                        onMouseDown={(e) => e.preventDefault()} // Prevent dropdown from closing when clicking header
                      >
                        <Typography
                          variant="body2"
                          className="text-sm font-medium text-gray-700"
                        >
                          Recent Searches
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={clearAllHistory}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }} // Prevent dropdown from closing
                          className="text-gray-400 text-xs hover:text-gray-600"
                          title="Clear all"
                          type="button"
                        >
                          <Typography variant="caption">clear all</Typography>
                        </IconButton>
                      </Box>
                    </>
                  )}
                <Box style={{ maxHeight: "432px", overflowY: "auto" }}>
                  {children}
                </Box>
              </Paper>
            )
        }
        onFocus={() => {
          if (!suggestionsDisabled) setOpen(true);
        }}
        onBlur={() => {
          if (!suggestionsDisabled) setOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleSubmit(searchValue);
            if (!suggestionsDisabled) setOpen(false);
          }
        }}
        open={suggestionsDisabled ? false : open}
        onOpen={() => !suggestionsDisabled && setOpen(true)}
        onClose={() => !suggestionsDisabled && setOpen(false)}
        className="flex-1"
      />
      {!isIntegrated && (
        <IconButton
          aria-label="Search"
          onClick={() => handleSubmit(searchValue)}
          size="small"
          className="ml-2"
        >
          <Search />
        </IconButton>
      )}
    </div>
  );
}

export default SearchBar;
